import {
  type StudentScoreEntity,
  ClassroomModuleResultSchema,
  ClassroomResultsArraySchema
} from '@/core/infrastructure/schemas/dashboard/ClassroomResultsSchema'
import { EntityValidator } from '@/core/infrastructure/utils/EntityValidator'
import { DashboardClassroomStudentResult } from './DashboardClassroomStudentResult'

/**
 * Classe représentant les résultats d'un module d'apprentissage pour toute une classe
 */
export class DashboardClassroomModuleResult {
  readonly learningCourseId: number
  readonly learningModuleId: number
  readonly learningModuleTitle: string
  readonly learningModuleOrder: number
  readonly displayOrder: number
  readonly visible: boolean
  readonly scores: DashboardClassroomStudentResult[]

  constructor(data: {
    learningCourseId: number
    learningModuleId: number
    learningModuleTitle: string
    learningModuleOrder: number
    displayOrder: number
    visible: boolean
    scores: StudentScoreEntity[]
  }) {
    this.learningCourseId = data.learningCourseId
    this.learningModuleId = data.learningModuleId
    this.learningModuleTitle = data.learningModuleTitle
    this.learningModuleOrder = data.learningModuleOrder
    this.displayOrder = data.displayOrder
    this.visible = data.visible
    this.scores = DashboardClassroomStudentResult.createMany(data.scores)
  }

  /**
   * Obtient le nombre d'étudiants qui ont commencé le diagnostic
   */
  public getStudentsStartedDiagnosticCount(): number {
    return this.scores.filter((score) => score.hasStartedDiagnostic()).length
  }

  /**
   * Obtient le nombre d'étudiants qui ont terminé le diagnostic
   */
  public getStudentsCompletedDiagnosticCount(): number {
    return this.scores.filter((score) => score.hasCompletedDiagnostic()).length
  }

  /**
   * Obtient la moyenne des scores de diagnostic début
   */
  public getAverageDiagStartScore(): number | null {
    const studentsWithScore = this.scores.filter((score) => score.diagScorePercentRounded !== null)
    if (studentsWithScore.length === 0) return null

    const total = studentsWithScore.reduce(
      (sum, score) => sum + (score.diagScorePercentRounded || 0),
      0
    )
    return Math.round(total / studentsWithScore.length)
  }

  /**
   * Obtient la moyenne des scores de diagnostic fin
   */
  public getAverageDiagEndScore(): number | null {
    const studentsWithScore = this.scores.filter(
      (score) => score.diagEndScorePercentRounded !== null
    )
    if (studentsWithScore.length === 0) return null

    const total = studentsWithScore.reduce(
      (sum, score) => sum + (score.diagEndScorePercentRounded || 0),
      0
    )
    return Math.round(total / studentsWithScore.length)
  }

  /**
   * Crée une instance à partir de données brutes
   */
  public static create(data: unknown): DashboardClassroomModuleResult {
    return EntityValidator.validateAndCreate(
      ClassroomModuleResultSchema,
      data,
      DashboardClassroomModuleResult,
      'ClassroomModuleResult'
    )
  }

  /**
   * Crée plusieurs instances à partir de données brutes
   */
  public static createMany(data: unknown[]): DashboardClassroomModuleResult[] {
    return EntityValidator.validateAndCreateMany(
      ClassroomResultsArraySchema,
      data,
      DashboardClassroomModuleResult,
      'ClassroomModuleResult'
    )
  }
}
